import { useState, useCallback } from 'react'
import { assignLocation, reassignLocation } from '../services/assignmentService'
import { useInventoryData } from './useInventoryData'
import { ShippingOrder, ShippingLineItem, StoreInventory } from '../DeShippingTableWrapper'

interface UseLocationAssignmentReturn {
  visible: boolean
  pendingChange: ShippingLineItem | null
  selectedLocation: string
  confirmLoading: boolean
  error: string | null
  inventoryState: {
    loading: boolean
    error: string | null
    data: StoreInventory[] | null
    hasInsufficientInventory: boolean
  }
  showDialog: (lineItem: ShippingLineItem) => void
  hideDialog: () => void
  setSelectedLocation: (location: string) => void
  confirmLocationChange: () => Promise<{ success: boolean, status: string } | undefined>
  cancel: () => void
  clearError: () => void
}

export function useLocationAssignment(): UseLocationAssignmentReturn {
  // Dialog state
  const [visible, setVisible] = useState(false)
  const [pendingChange, setPendingChange] = useState<ShippingLineItem | null>(null)
  const [selectedLocation, setSelectedLocation] = useState<string>('')
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Use inventory hook, but only enable it when we have a pending change
  const inventoryData = useInventoryData({
    variantId: pendingChange?.variant_id ? String(pendingChange.variant_id) : '',
    quantity: pendingChange?.product_quantity || 1,
    enabled: !!pendingChange
  })

  // Show location change dialog
  const showDialog = useCallback((lineItem: ShippingLineItem) => {
    setPendingChange(lineItem)
    setSelectedLocation('')
    setVisible(true)
    setError(null)
  }, [])

  // Hide dialog
  const hideDialog = useCallback(() => {
    setVisible(false)
    setSelectedLocation('')
    setError(null)
  }, [])

  // Clear error state
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  // Confirm location change
  const confirmLocationChange = useCallback(async () => {
    if (!pendingChange || !selectedLocation) return

    try {
      setConfirmLoading(true)
      setError(null)

      let response

      if (pendingChange.fulfillment_status === 'unassigned') {
        response = await assignLocation(
          pendingChange.line_item_id, 
          selectedLocation
        )
      } else {
        response = await reassignLocation(
          pendingChange.line_item_id, 
          selectedLocation
        )
      }
      
      setVisible(false)
      setPendingChange(null)
      setSelectedLocation('')
      
      return response
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to assign location'
      setError(errorMessage)
    } finally {
      setConfirmLoading(false)
    }
  }, [pendingChange, selectedLocation])

  // Cancel location change
  const cancel = useCallback(() => {
    setVisible(false)
    setPendingChange(null)
    setSelectedLocation('')
    setError(null)
  }, [])

  return {
    visible,
    pendingChange,
    selectedLocation,
    confirmLoading,
    error,
    inventoryState: {
      loading: inventoryData.loading,
      error: inventoryData.error,
      data: inventoryData.data,
      hasInsufficientInventory: inventoryData.hasInsufficientInventory
    },
    showDialog,
    hideDialog,
    setSelectedLocation,
    confirmLocationChange,
    cancel,
    clearError
  }
}